import { Component, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Trip, TripService } from '../../core/services/trip.service';
import { UserProfile, UserService } from '../../core/services/user.service';
import { ToastComponent } from '../../shared/components/toast/toast.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-trip-members-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatListModule,
        MatSnackBarModule
    ],
    template: `
    <h2 mat-dialog-title>Manage Trip Members</h2>
    <mat-dialog-content>
      <p style="margin-bottom: 16px; color: #666; font-size: 0.9rem;">
        Members can see expenses and add their own for this trip.
      </p>

      <!-- Member List -->
      <mat-list>
        <div mat-subheader>Current Members</div>
        <mat-list-item *ngFor="let memberId of data.trip.members">
            <mat-icon matListItemIcon>person</mat-icon>
            <div matListItemTitle class="member-row">
                <div class="member-info">
                    <ng-container *ngIf="(getProfile(memberId) | async) as profile; else loading">
                        {{ profile.displayName }} 
                        <span *ngIf="memberId === data.trip.createdBy" style="font-size: 0.8em; color: green;">(Owner)</span>
                    </ng-container>
                    <ng-template #loading>Loading...</ng-template>
                </div>

                <!-- Remove Button (Only Owner can remove others) -->
                 <button mat-icon-button class="delete-member-btn" 
                        *ngIf="canRemove(memberId)"
                        (click)="removeMember(memberId)"
                        [disabled]="isLoading">
                    <mat-icon>delete</mat-icon>
                </button>
            </div>
        </mat-list-item>
      </mat-list>

      <div class="divider"></div>

      <!-- Add Member Form -->
      <div class="add-section" *ngIf="isOwner">
        <h4>Add Member</h4>
        <div class="add-row">
            <mat-form-field appearance="outline" class="email-input">
                <mat-label>Email Address</mat-label>
                <input matInput [(ngModel)]="inviteEmail" placeholder="friend@example.com">
            </mat-form-field>
            <button mat-flat-button color="primary" (click)="addMember()" [disabled]="!inviteEmail || isLoading">
                <mat-icon>person_add</mat-icon>
                Add
            </button>
        </div>
      </div>

    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Close</button>
    </mat-dialog-actions>
  `,
    styles: [`
    .divider {
        height: 1px;
        background: #eee;
        margin: 16px 0;
    }
    .add-section h4 { margin-top: 0; margin-bottom: 8px; }
    .add-row {
        display: flex;
        gap: 8px;
        align-items: center;
    }
    .email-input { flex: 1; margin-bottom: -1.25em; }
    
    .delete-member-btn {
        color: #FF5252 !important; /* Premium Red */
        background: rgba(255, 82, 82, 0.1) !important; /* Soft Red Bg */
        width: 40px !important;
        height: 40px !important;
        border-radius: 12px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        margin-left: auto !important; /* Ensure it pushes to the right */
        flex-shrink: 0 !important; /* Prevent shrinking */
    }
    /* Ensure list item content is flex row */
    .member-row {
        display: flex !important;
        align-items: center !important;
        justify-content: space-between !important;
        width: 100% !important;
    }

    .member-info {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding-right: 8px;
    }
  `]
})
export class TripMembersDialogComponent {
    userService = inject(UserService);
    tripService = inject(TripService);
    snackBar = inject(MatSnackBar);
    dialog = inject(MatDialog);

    inviteEmail = '';
    isLoading = false;
    currentUserId = this.userService.currentProfileSnapshot?.uid;

    // Cache for user profiles
    profileCache = new Map<string, Promise<UserProfile | null>>();

    constructor(
        public dialogRef: MatDialogRef<TripMembersDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { trip: Trip }
    ) { }

    get isOwner(): boolean {
        return this.currentUserId === this.data.trip.createdBy;
    }

    getProfile(uid: string): Promise<UserProfile | null> {
        if (this.profileCache.has(uid)) {
            return this.profileCache.get(uid)!;
        }
        const promise = this.userService.getProfile(uid);
        this.profileCache.set(uid, promise);
        return promise;
    }

    canRemove(memberId: string): boolean {
        // Only owner can remove
        if (this.currentUserId !== this.data.trip.createdBy) return false;
        // Cannot remove self
        if (memberId === this.currentUserId) return false;
        return true;
    }

    async addMember() {
        if (!this.inviteEmail.trim()) return;
        this.isLoading = true;
        try {
            await this.tripService.addMemberByEmail(this.data.trip.id!, this.inviteEmail.trim());
            this.showToast('Success', 'Member added to trip!', 'success');
            this.inviteEmail = '';
            this.dialogRef.close(true);
        } catch (err: any) {
            this.showToast('Error', err.message, 'error');
        } finally {
            this.isLoading = false;
        }
    }

    async removeMember(memberId: string) {
        // Use Custom Confirm Dialog
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Remove Member',
                message: 'Are you sure you want to remove this member from the trip?'
            }
        });

        const result = await firstValueFrom(dialogRef.afterClosed());
        if (!result) return;

        this.isLoading = true;
        try {
            await this.tripService.removeMember(this.data.trip.id!, memberId);
            this.showToast('Success', 'Member removed.', 'success');
            // Remove locally
            this.data.trip.members = this.data.trip.members?.filter(m => m !== memberId);
        } catch (err: any) {
            this.showToast('Error', err.message, 'error');
        } finally {
            this.isLoading = false;
        }
    }

    private showToast(title: string, message: string, type: 'success' | 'error') {
        this.snackBar.openFromComponent(ToastComponent, {
            data: { title, message, type },
            duration: 3000,
            panelClass: 'custom-toast-container',
            horizontalPosition: 'end',
            verticalPosition: 'bottom'
        });
    }
}
